import type { Encadernacao, Formato, LadoLombada, ModoSemana, Orientacao } from '@miolos/core';
import {
  DESCRICAO_ENCADERNACAO,
  DESCRICAO_FIM_DE_SEMANA_DIVIDIDO,
  DESCRICAO_LOMBADA,
  DESCRICAO_ORIENTACAO,
  DESCRICAO_SANGRIA,
  DESCRICAO_SEMANA,
  ENCADERNACOES,
  FORMATOS,
  LADOS_LOMBADA,
  ORIENTACOES,
  PAPEIS,
  ROTULO_ENCADERNACAO,
  ROTULO_FIM_DE_SEMANA_DIVIDIDO,
  ROTULO_LOMBADA,
  ROTULO_ORIENTACAO,
  ROTULO_SANGRIA,
  ROTULO_SEMANA,
  SANGRIA_DE_CORTE_MM,
  SEMANAS,
  ajustarParaFormato,
  dimensoesDaFolha,
  medirPaginaDoMiolo,
} from '@miolos/core';
import { CartaoOpcao } from '@/components/CartaoOpcao';
import { Campo } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LinhaSwitch, Slider } from '@/components/ui/switch';
import { useEditor } from '../EstadoEditor';
import { Grade, MockPagina, TituloPasso } from '../ui';

/* ------------------------------------------------------------------ */
/* 1. Tamanho do papel                                                 */
/* ------------------------------------------------------------------ */

const DICAS_FORMATO: Record<Formato, string> = {
  A4: 'Muito espaço para escrever. Ótimo para planejamento detalhado, atas e diário. Fica grande na bolsa.',
  A5: 'O equilíbrio mais procurado: cabe na mochila e ainda sobra área de escrita confortável.',
  A6: 'Compacto, de bolso. Ideal para listas e lembretes rápidos — o espaço é disputado.',
  SLIM: 'Estreito e comprido, feito para listas e planejamento em coluna. Cabe em qualquer bolsa pequena.',
  QUADRADO: 'Folha curta e larga: bom para quem escreve em blocos, listas lado a lado e desenho.',
  BIG: 'Um pouco maior que o A5, com mais largura para escrever — o meio-termo entre a pasta e a mesa.',
  UNIVERSITARIO: 'A folha mais alta: bastante espaço por dia para quem escreve muito. Pede uma capa maior.',
};

export function PassoFormato() {
  const { config, definirConfig, definirPapel } = useEditor();

  /**
   * Escolher a encadernação também ajusta a margem da lombada.
   *
   * Wire-o e espiral precisam de espaço para o furo: sem uns 18 mm o arame come
   * o começo do texto. Só mexemos na margem quando ela está no padrão do
   * formato — se o cliente já escolheu uma medida, a escolha dele é que vale.
   */
  const escolherEncadernacao = (encadernacao: Encadernacao) => {
    const precisaDeFuro = encadernacao === 'WIRE_O' || encadernacao === 'ESPIRAL';
    definirPapel({
      encadernacao,
      ...(precisaDeFuro && config.papel.margemLombadaMm === 0
        ? { margemLombadaMm: 18 }
        : {}),
      ...(precisaDeFuro ? {} : { guiaDeFuro: false }),
    });
  };
  const medidas = medirPaginaDoMiolo(config);

  return (
    <div className="space-y-5">
      <TituloPasso
        titulo="Qual tamanho da sua agenda?"
        descricao="O tamanho define quanto espaço você terá para escrever em cada dia. Dá para mudar depois, mas o miolo inteiro é recalculado."
      />
      <Grade colunas={3}>
        {FORMATOS.map((formato) => {
          const papel = PAPEIS[formato];
          const folha = dimensoesDaFolha(formato, config.papel.orientacao);
          return (
            <CartaoOpcao
              key={formato}
              selecionado={config.formato === formato}
              titulo={`${formato} · ${folha.larguraMm} × ${folha.alturaMm} mm`}
              descricao={DICAS_FORMATO[formato]}
              aoSelecionar={() => definirConfig(ajustarParaFormato(config, formato))}
            >
              <div className="flex items-center gap-3 pt-1 text-primary">
                <MockPagina largura={papel.larguraMm} altura={papel.alturaMm} faixas={1} orientacao={config.papel.orientacao} />
                <span className="text-xs text-muted-foreground">
                  {config.papel.orientacao === 'PAISAGEM' ? 'deitado' : 'em pé'}
                </span>
              </div>
            </CartaoOpcao>
          );
        })}
      </Grade>

      {/* Orientação da folha */}
      <section className="space-y-4 rounded-lg border border-border bg-card p-4">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">Como a folha fica na mão</h3>
          <p className="text-xs leading-relaxed text-muted-foreground">
            A orientação muda a proporção de todas as páginas do miolo — inclusive das capas, dos cadernos e das
            páginas que você enviar como fundo.
          </p>
        </div>

        <Grade colunas={2}>
          {ORIENTACOES.map((orientacao) => {
            const folha = dimensoesDaFolha(config.formato, orientacao);
            return (
              <CartaoOpcao
                key={orientacao}
                selecionado={config.papel.orientacao === orientacao}
                titulo={ROTULO_ORIENTACAO[orientacao]}
                descricao={DESCRICAO_ORIENTACAO[orientacao]}
                aoSelecionar={() => definirPapel({ orientacao })}
              >
                <div className="flex items-center gap-3 pt-1 text-primary">
                  <MockPagina
                    largura={PAPEIS[config.formato].larguraMm}
                    altura={PAPEIS[config.formato].alturaMm}
                    faixas={1}
                    orientacao={orientacao}
                  />
                  <span className="text-xs text-muted-foreground">
                    {folha.larguraMm} × {folha.alturaMm} mm
                  </span>
                </div>
              </CartaoOpcao>
            );
          })}
        </Grade>
      </section>

      {/* Margem de encadernação */}
      <section className="space-y-4 rounded-lg border border-border bg-card p-4">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold">Onde fica a margem da encadernação</h3>
          <p className="text-xs leading-relaxed text-muted-foreground">
            É a margem maior, do lado onde o miolo é preso. Ela evita que o texto entre no vinco — e é o que você
            escolhe aqui.
          </p>
        </div>

        <Campo rotulo="Posição da lombada" dica={DESCRICAO_LOMBADA[config.papel.ladoLombada]}>
          <Select
            value={config.papel.ladoLombada}
            onValueChange={(valor) => definirPapel({ ladoLombada: valor as LadoLombada })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LADOS_LOMBADA.map((lado) => (
                <SelectItem key={lado} value={lado}>
                  {ROTULO_LOMBADA[lado]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>

        <Campo
          rotulo="Encadernação na prévia"
          dica={
            config.papel.encadernacao === 'NENHUMA'
              ? 'Só um desenho para você ver como o miolo vai ficar montado. Escolher a encadernação ajusta a margem da lombada para o furo caber.'
              : `${DESCRICAO_ENCADERNACAO[config.papel.encadernacao]} A representação não sai na impressão.`
          }
        >
          <Select
            value={config.papel.encadernacao}
            onValueChange={(valor) => escolherEncadernacao(valor as Encadernacao)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENCADERNACOES.map((encadernacao) => (
                <SelectItem key={encadernacao} value={encadernacao}>
                  {ROTULO_ENCADERNACAO[encadernacao]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Campo>

        {config.papel.encadernacao === 'WIRE_O' || config.papel.encadernacao === 'ESPIRAL' ? (
          <LinhaSwitch
            rotulo="Imprimir a guia de furo na margem"
            descricao="A marca sai impressa e continua visível no miolo encadernado: serve para a encadernadora alinhar o furo pela linha. Desligada, o arquivo sai limpo."
            checked={config.papel.guiaDeFuro}
            onCheckedChange={(ligado: boolean) => definirPapel({ guiaDeFuro: ligado })}
          />
        ) : null}

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Margem da lombada</span>
            <span className="font-mono text-xs text-muted-foreground">
              {config.papel.margemLombadaMm > 0 ? `${config.papel.margemLombadaMm} mm` : 'padrão do formato'}
            </span>
          </div>
          <Slider
            value={[config.papel.margemLombadaMm]}
            min={0}
            max={30}
            step={0.5}
            onValueChange={([valor]) => definirPapel({ margemLombadaMm: valor ?? 0 })}
          />
          <div className="flex flex-wrap gap-2 pt-1">
            {[0, 12, 16, 20, 25].map((valor) => (
              <button
                key={valor}
                type="button"
                onClick={() => definirPapel({ margemLombadaMm: valor })}
                className={[
                  'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  config.papel.margemLombadaMm === valor
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-card hover:border-primary/40',
                ].join(' ')}
              >
                {valor === 0 ? 'padrão' : `${valor} mm`}
              </button>
            ))}
          </div>
        </div>

        <p className="rounded-md bg-muted/60 p-3 text-xs leading-relaxed text-muted-foreground">
          Nesta configuração, a área útil de cada página fica com{' '}
          <strong className="text-foreground">
            {medidas.larguraConteudoMm.toFixed(0)} × {medidas.alturaConteudoMm.toFixed(0)} mm
          </strong>{' '}
          para escrever.
        </p>
      </section>

      {/* Sangria para gráfica */}
      <section className="space-y-4 rounded-lg border border-border bg-card p-4">
        <LinhaSwitch
          id="sangria"
          rotulo={ROTULO_SANGRIA}
          descricao={DESCRICAO_SANGRIA}
          checked={(config.sangriaMm ?? 0) > 0}
          onCheckedChange={(ligada) =>
            definirConfig({ ...config, sangriaMm: ligada ? SANGRIA_DE_CORTE_MM : 0 })
          }
        />
        {medidas.sangriaMm > 0 ? (
          <p className="text-xs leading-relaxed text-muted-foreground">
            O arquivo sai com <strong className="text-foreground">{medidas.larguraMm} × {medidas.alturaMm} mm</strong> — a
            folha aparada continua com {medidas.larguraAparadaMm} × {medidas.alturaAparadaMm} mm — e as marcas de corte
            ficam nos quatro cantos.
          </p>
        ) : null}
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 2. Dias por página                                                  */
/* ------------------------------------------------------------------ */

export function PassoDiasPorPagina() {
  const { config, atualizar, documento } = useEditor();
  const papel = PAPEIS[config.formato];
  const semanal = config.paginasDeDia === 'SEMANA';

  return (
    <div className="space-y-5">
      <TituloPasso
        titulo="Como a agenda conta os dias?"
        descricao="Um dia por página dá mais respiro para escrever. Dois dias economizam papel. No formato semanal cabem os sete dias na mesma folha — e você também pode tirar as páginas de dia e fazer um caderno só de folhas para escrever. Com um dia por página ainda dá para dividir o fim de semana: sábado e domingo na mesma folha."
      />

      <Grade colunas={2}>
        <CartaoOpcao
          selecionado={
            config.paginasDeDia === 'DIA' && config.diasPorPagina === 1 && config.fimDeSemanaDividido !== true
          }
          titulo="1 dia por página"
          descricao={`Área de escrita estimada: ${documento.resumo.alturaEscritaMm} mm de altura por dia.`}
          aoSelecionar={() => atualizar({ paginasDeDia: 'DIA', diasPorPagina: 1, fimDeSemanaDividido: false })}
        >
          <div className="flex gap-2 pt-1 text-primary">
            <MockPagina largura={papel.larguraMm} altura={papel.alturaMm} faixas={1} />
          </div>
        </CartaoOpcao>

        {/*
          A opção só aparece com um dia por página: com dois dias por página o
          sábado e o domingo já saem na mesma folha.
        */}
        {config.paginasDeDia === 'DIA' && config.diasPorPagina === 1 ? (
          <CartaoOpcao
            selecionado={config.fimDeSemanaDividido === true}
            titulo={ROTULO_FIM_DE_SEMANA_DIVIDIDO}
            descricao={DESCRICAO_FIM_DE_SEMANA_DIVIDIDO}
            aoSelecionar={() => atualizar({ paginasDeDia: 'DIA', diasPorPagina: 1, fimDeSemanaDividido: true })}
          >
            <div className="flex gap-2 pt-1 text-primary">
              <MockPagina largura={papel.larguraMm} altura={papel.alturaMm} faixas={1} />
              <MockPagina largura={papel.larguraMm} altura={papel.alturaMm} faixas={2} compacto />
            </div>
          </CartaoOpcao>
        ) : null}

        <CartaoOpcao
          selecionado={config.paginasDeDia === 'DIA' && config.diasPorPagina === 2}
          titulo="2 dias por página"
          descricao="Divisão em cima e embaixo. Boa escolha para rotina corrida e muitos compromissos."
          aoSelecionar={() => atualizar({ paginasDeDia: 'DIA', diasPorPagina: 2, fimDeSemanaDividido: false })}
        >
          <div className="flex gap-2 pt-1 text-primary">
            <MockPagina largura={papel.larguraMm} altura={papel.alturaMm} faixas={2} compacto />
          </div>
        </CartaoOpcao>

        <CartaoOpcao
          selecionado={semanal}
          titulo="7 dias por página (semana)"
          descricao="Segunda a domingo na mesma folha, cada dia com o seu espaço de escrita. Bom para quem escreve pouco por dia e quer a semana inteira à vista."
          aoSelecionar={() => atualizar({ paginasDeDia: 'SEMANA', fimDeSemanaDividido: false })}
        >
          <div className="flex gap-2 pt-1 text-primary">
            <MockPagina largura={papel.larguraMm} altura={papel.alturaMm} faixas={7} compacto />
          </div>
        </CartaoOpcao>

        <CartaoOpcao
          selecionado={config.paginasDeDia === 'NENHUM'}
          titulo="Sem páginas de dia"
          descricao="O miolo sai sem nenhuma página de dia: só as páginas extras e os cadernos. É o caminho para montar um caderno apenas com folhas pautadas."
          aoSelecionar={() => atualizar({ paginasDeDia: 'NENHUM', fimDeSemanaDividido: false })}
        >
          <div className="flex gap-2 pt-1 text-primary">
            <MockPagina largura={papel.larguraMm} altura={papel.alturaMm} faixas={1} compacto />
          </div>
        </CartaoOpcao>
      </Grade>

      {config.paginasDeDia === 'NENHUM' ? (
        <p className="rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
          O miolo fica sem páginas de dia. Os <strong className="text-foreground">calendários extras</strong> que
          você marcou no passo <strong className="text-foreground">Páginas extras</strong> (visão do ano e
          divisórias de mês) continuam entrando — só o mini calendário do mês, que mora dentro da página de dia,
          deixa de aparecer.
        </p>
      ) : null}

      {semanal ? (
        <div className="space-y-2 rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
          <p>
            No formato semanal, o campo de <strong className="text-foreground">páginas de dia</strong> do passo
            Período passa a valer <strong className="text-foreground">semanas</strong> — cada semana é uma folha com os
            sete dias.
          </p>
          {PAPEIS[config.formato].estreito ? (
            <p className="text-destructive">
              Em {config.formato}, no formato semanal, cada dia fica com pouco mais de 20 mm. Para escrever à mão
              com conforto, prefira uma folha maior.
            </p>
          ) : null}
        </div>
      ) : null}

      {config.paginasDeDia === 'NENHUM' ? (
        <div className="space-y-2 rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
          <p>
            Sem páginas de dia, o miolo é montado com o que você escolher em{' '}
            <strong className="text-foreground">Páginas extras</strong> e em{' '}
            <strong className="text-foreground">Cadernos</strong>. Para um caderno de folhas pautadas, adicione o
            caderno <strong className="text-foreground">Folhas para escrever</strong> e escolha os blocos (linha cheia,
            tracejada, pontilhada, quadriculada...).
          </p>
          <p>
            Os passos <strong className="text-foreground">Dias da semana</strong>,{' '}
            <strong className="text-foreground">Período</strong> e{' '}
            <strong className="text-foreground">Quadros do dia</strong> saem do assistente, porque não há data para
            configurar. A visão do ano e os divisores de mês também saem do miolo — se você ligar qualquer um dos
            dois em Páginas extras, o passo Período volta.
          </p>
        </div>
      ) : null}

      {config.paginasDeDia === 'DIA' ? (
        <div className="rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
          Você escolheu <strong className="text-foreground">{config.formato}</strong> com{' '}
          <strong className="text-foreground">{config.diasPorPagina} dia(s) por página</strong>. Cada dia fica com
          cerca de {documento.resumo.alturaEscritaMm} mm de altura útil.
          {config.fimDeSemanaDividido && config.diasPorPagina === 1 ? (
            <>
              {' '}
              No fim de semana dividido, sábado e domingo ficam na mesma folha, cada um com cerca de{' '}
              <strong className="text-foreground">metade dessa altura</strong> — e a agenda fica com menos
              páginas do que teria com uma folha para cada dia.
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 3. Dias da semana                                                   */
/* ------------------------------------------------------------------ */

export function PassoSemana() {
  const { config, atualizar } = useEditor();

  return (
    <div className="space-y-5">
      <TituloPasso
        titulo="Quais dias vão aparecer?"
        descricao="Se a agenda é para o trabalho, cortar sábado e domingo reduz bastante o número de páginas."
      />
      <Grade colunas={2}>
        {SEMANAS.map((modo: ModoSemana) => (
          <CartaoOpcao
            key={modo}
            selecionado={config.semana === modo}
            titulo={ROTULO_SEMANA[modo]}
            descricao={DESCRICAO_SEMANA[modo]}
            aoSelecionar={() => atualizar({ semana: modo })}
          />
        ))}
      </Grade>
    </div>
  );
}
